/**
 * WitnessEnter — entrance animation wrapper for witness-shaped surfaces.
 *
 * Wraps an arbitrary child (typically a `WitnessCard`) and plays a soft
 * fade + rise on mount. When the consumer holds a single slot but swaps
 * the witness identity, pass `triggerKey={witness.id}` so each new
 * witness replays the entrance.
 *
 * Honors `prefers-reduced-motion`: under reduced motion the wrapper
 * renders its child immediately with no animation.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { motion, useReducedMotion } from "motion/react";
import { entranceVariants } from "../../styling/motionTokens.js";

export interface WitnessEnterProps {
  /** Identity of the currently-displayed witness — change to replay. */
  readonly triggerKey?: string | number;
  /** Wrapped surface (typically a `WitnessCard`). */
  readonly children: React.ReactNode;
  /** Optional className passthrough on the animated wrapper. */
  readonly className?: string;
}

export const WitnessEnter = React.memo(function WitnessEnter({
  triggerKey,
  children,
  className,
}: WitnessEnterProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }
  // The motion `key` is what makes a `triggerKey` change replay the
  // entrance: React remounts the element, motion sees a fresh node and
  // animates from `initial` → `enter`. An `AnimatePresence` wrapper
  // would be needed only for an exit animation; consumers that need an
  // exit can wrap `<WitnessEnter>` in their own `<AnimatePresence>`.
  const slotKey = triggerKey ?? "witness-enter";
  return (
    <motion.div
      key={slotKey}
      className={className}
      data-testid="witness-enter"
      data-trigger-key={String(slotKey)}
      variants={entranceVariants}
      initial="initial"
      animate="enter"
    >
      {children}
    </motion.div>
  );
});
